// components/UniversalPreviewModal.tsx
import React from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn, ZoomOut, Download, Printer, Settings, FileSpreadsheet } from 'lucide-react';
import { useUniversalPreview } from '../hooks/useUniversalPreview';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Label as RechartsLabel, Cell, ReferenceArea } from 'recharts';
import '../styles/UniversalPreview.css';

// Sayfa boyutları ve hesaplamalar
const PAGE_DIMENSIONS = {
  portrait: { width: 210, height: 297 },
  landscape: { width: 297, height: 210 }
};

const MARGINS = { top: 15, bottom: 15, left: 15, right: 15 };
const HEADER_HEIGHT = 20;
const FOOTER_HEIGHT = 8;

// Dinamik satır yüksekliği hesaplama - optimize edildi
const estimateRowHeight = (content: string, columnWidth: number): number => {
  const baseHeight = 3; // mm - daha küçük
  const charsPerLine = Math.floor(columnWidth / 1.2); // Daha fazla karakter
  const lines = Math.ceil(content.length / charsPerLine);
  return Math.max(baseHeight, lines * 2.5); // Daha az yükseklik
};

// Sayfa başına maksimum yükseklik hesaplama - daha fazla alan
const getAvailableHeight = (format: string): number => {
  const pageHeight = format === 'landscape' ? PAGE_DIMENSIONS.landscape.height : PAGE_DIMENSIONS.portrait.height;
  return pageHeight - MARGINS.top - MARGINS.bottom - HEADER_HEIGHT - FOOTER_HEIGHT - 5; // Ekstra margin azaltıldı
};

// Tablo verilerini sayfalara bölme - optimize edildi
const paginateTableData = (data: any[], format: string, hasTopicHeaders: boolean) => {
  const availableHeight = getAvailableHeight(format);
  const rowsPerPage = 35; // Sayfa başına maksimum satır sayısı artırıldı
  const pages: any[][] = [];
  
  // TSRS verisi için basit sayfalama
  if (Array.isArray(data) && data.length > 0 && data[0]?.displayNo && data[0]?.soruReferans) {
    for (let i = 0; i < data.length; i += rowsPerPage) {
      pages.push(data.slice(i, i + rowsPerPage));
    }
    return pages.length > 0 ? pages : [data];
  }
  
  // Kategori bazlı TSRS verisi (GRI Genel gibi)
  if (Array.isArray(data) && data.length > 0 && data[0].category) {
    let allRows: any[] = [];
    
    data.forEach((categoryData, catIndex) => {
      const items = categoryData.items || [];
      items.forEach((item: any) => {
        allRows.push({
          ...item,
          category: categoryData.category,
          isFirstInCategory: allRows.length === 0 || allRows[allRows.length - 1].category !== categoryData.category
        });
      });
    });
    
    // Sayfalara böl - kategori başlıklarını koruyarak
    let currentPage: any[] = [];
    let currentCategory = '';
    
    allRows.forEach((row, index) => {
      if (currentPage.length >= rowsPerPage) {
        pages.push([...currentPage]);
        currentPage = [];
        
        // Yeni sayfada kategori başlığını tekrar ekle
        if (row.category !== currentCategory) {
          currentPage.push({ ...row, isFirstInCategory: true });
          currentCategory = row.category;
        } else {
          currentPage.push(row);
        }
      } else {
        currentPage.push(row);
        if (row.isFirstInCategory) {
          currentCategory = row.category;
        }
      }
    });
    
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    return pages.length > 0 ? pages : [data];
  }
  
  // Normal tablo verisi için - Veri Toplama formatı
  if (hasTopicHeaders) {
    let currentPage: any[] = [];
    let currentHeight = 0;
    let currentTopic = '';
    let rowCount = 0;
    const maxRowsPerPage = 30; // Sayfa başına maksimum satır artırıldı
    
    data.forEach((item, index) => {
      // Konu başlığı
      if (item.konuAdi && !item['Soru No']) {
        // Eğer sayfa doluysa ve yeni konu başlıyorsa, yeni sayfa aç
        if (rowCount > maxRowsPerPage * 0.8 && currentPage.length > 0) {
          pages.push([...currentPage]);
          currentPage = [];
          rowCount = 0;
        }
        
        currentTopic = item.konuAdi;
        currentPage.push(item);
        return;
      }
      
      // Normal satır
      rowCount++;
      
      // Sayfa doluysa yeni sayfa aç
      if (rowCount > maxRowsPerPage && currentPage.length > 5) {
        pages.push([...currentPage]);
        currentPage = [];
        rowCount = 0;
        
        // Yeni sayfada konu başlığını tekrar ekle
        if (currentTopic) {
          currentPage.push({ konuAdi: currentTopic });
        }
      }
      
      currentPage.push(item);
    });
    
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    return pages.length > 0 ? pages : [data];
  }
  
  // Başlıksız tablo için basit sayfalama
  for (let i = 0; i < data.length; i += 35) {
    pages.push(data.slice(i, i + 35));
  }
  
  return pages.length > 0 ? pages : [data];
};

const UniversalPreviewModal: React.FC = () => {
  const { isOpen, data, options, isLoading, close, updateOptions, downloadPDF, downloadExcel, print } = useUniversalPreview();

  // Early returns with proper null checks
  if (!isOpen) return null;
  if (!data) return null;
  if (!options) return null;

  // Çoklu sayfa desteği ile content hesaplama
  const paginatedContent = (() => {
    if (!data || !data.content) {
      return [<div key="empty">İçerik bulunamadı</div>];
    }
    
    // React element ise - bileşenlere göre sayfalara böl
    if (React.isValidElement(data.content)) {
      const contentDiv = data.content as React.ReactElement;
      if (contentDiv.props?.children) {
        const children = React.Children.toArray(contentDiv.props.children);
        const pages: React.ReactNode[] = [];
        
        // İlk 3 bileşen (metrikler, durum, grafik) = 1. sayfa
        // Tablo = 2. sayfa
        // En iyi/kötü performans = 2. sayfanın sonuna
        if (children.length >= 4) {
          // 1. Sayfa: İlk 3 bileşen
          pages.push(
            <div key="page-0" style={{ fontFamily: 'Arial, sans-serif' }}>
              {children.slice(0, 3)}
            </div>
          );
          // 2. Sayfa: Tablo + performans kartları
          pages.push(
            <div key="page-1" style={{ fontFamily: 'Arial, sans-serif' }}>
              {children.slice(3)}
            </div>
          );
        } else {
          // Tüm içeriği tek sayfada göster
          pages.push(data.content);
        }
        
        return pages;
      }
      return [data.content];
    }
    
    // Eğer tablo verisi ise sayfalara böl
    if (Array.isArray(data.content) && data.content.length > 0) {
      // Kategori bazlı veri mi kontrol et
      if (data.content[0]?.category) {
        const pages = paginateTableData(data.content, options?.format || 'portrait', false);
        return pages;
      }
      
      const hasHeaders = data.content.some((item: any) => item.konuAdi && !item['Soru No']);
      const pages = paginateTableData(data.content, options?.format || 'portrait', hasHeaders);
      return pages;
    }
    
    return [data.content];
  })();

  const renderChart = () => {
    if (!data.chartData || !data.chartConfig) return null;
    
    const { chartData, chartConfig } = data;
    const { xAxisLabel, yAxisLabel, thresholds } = chartConfig;
    
    return (
      <div style={{ width: '100%', height: '500px', marginTop: '20px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px', textAlign: 'center' }}>Çifte Önemlilik Matrisi Grafiği</h3>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 25, right: 60, bottom: 55, left: 35 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis 
              type="number" 
              dataKey="x" 
              name={xAxisLabel}
              label={{ value: xAxisLabel, position: 'insideBottom', offset: -30, fill: '#c0392b', fontSize: 12, fontWeight: '600' }}
              domain={[0, Math.max(25, ...chartData.map((d: any) => d.x)) + 5]}
              ticks={[0, thresholds.finans_onemsiz, thresholds.finans_hayati, Math.max(25, ...chartData.map((d: any) => d.x))]}
              stroke="#c0392b"
              axisLine={{ stroke: '#c0392b', strokeWidth: 1.5 }}
              tick={{ fill: '#a93226', fontSize: 10, fontWeight: 500 }}
            />
            <YAxis 
              type="number" 
              dataKey="y" 
              name={yAxisLabel}
              label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', offset: -25, fill: '#2980b9', fontSize: 12, fontWeight: '600' }}
              domain={[0, Math.max(125, ...chartData.map((d: any) => d.y)) + 20]}
              ticks={[0, thresholds.etki_onemsiz, thresholds.etki_hayati, Math.max(125, ...chartData.map((d: any) => d.y))]}
              stroke="#2980b9"
              axisLine={{ stroke: '#2980b9', strokeWidth: 1.5 }}
              tick={{ fill: '#2471a3', fontSize: 10, fontWeight: 500 }}
            />
            <ZAxis type="number" dataKey="z" range={[120, 550]} name="Boyut" />
            <Tooltip 
              cursor={{ strokeDasharray: '3 3', stroke: '#b0bec5' }}
              wrapperStyle={{ zIndex: 1000 }}
              contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #cccccc', borderRadius: '8px', padding: '12px 15px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const pointData = payload[0].payload;
                  return (
                    <div style={{ fontSize: '10px' }}>
                      <p style={{ margin: 0, fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>
                        {`${pointData.s_no}. ${pointData.name}`}
                      </p>
                      <p style={{ margin: 0, color: '#2980b9' }}>
                        {`${yAxisLabel}: ${pointData.y}`}
                      </p>
                      <p style={{ margin: 0, color: '#c0392b', marginTop: '2px' }}>
                        {`${xAxisLabel}: ${pointData.x}`}
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <ReferenceArea x1={0} x2={thresholds.finans_onemsiz} y1={0} y2={thresholds.etki_onemsiz} stroke="none" fill="#EF4444" fillOpacity={0.1} />
            <ReferenceArea x1={thresholds.finans_hayati} x2={Math.max(30, ...chartData.map((d: any) => d.x)) + 5} y1={thresholds.etki_hayati} y2={Math.max(160, ...chartData.map((d: any) => d.y)) + 5} stroke="none" fill="#10B981" fillOpacity={0.15} />
            <ReferenceArea x1={thresholds.finans_onemsiz} x2={thresholds.finans_hayati} y1={thresholds.etki_onemsiz} y2={thresholds.etki_hayati} stroke="none" fill="#F59E0B" fillOpacity={0.08} />
            <Scatter name="Önemli Konular" data={chartData} shape="circle" label={{ position: 'center', dataKey: 's_no', fill: '#fff', fontSize: 10, fontWeight: 'bold' }}>
              {chartData.map((entry: any, index: number) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Scatter>
            <ReferenceLine y={thresholds.etki_hayati} stroke="#2980b9" strokeDasharray="5 3" strokeWidth={1.5}>
              <RechartsLabel value={`Etki Hayati (${thresholds.etki_hayati})`} position="insideTopRight" fill="#2471a3" fontSize={10} dy={-6} dx={5} fontWeight="600" />
            </ReferenceLine>
            <ReferenceLine y={thresholds.etki_onemsiz} stroke="#7f8c8d" strokeDasharray="3 3" strokeWidth={1.2}>
              <RechartsLabel value={`Etki (${thresholds.etki_onemsiz})`} position="insideTopRight" fill="#616A6B" fontSize={10} dy={6} dx={5} fontWeight="600" />
            </ReferenceLine>
            <ReferenceLine x={thresholds.finans_hayati} stroke="#c0392b" strokeDasharray="5 3" strokeWidth={1.5}>
              <RechartsLabel value={`Fin. Hayati (${thresholds.finans_hayati})`} position="insideTopRight" fill="#a93226" fontSize={10} dx={5} dy={-18} fontWeight="600" />
            </ReferenceLine>
            <ReferenceLine x={thresholds.finans_onemsiz} stroke="#95a5a6" strokeDasharray="3 3" strokeWidth={1.2}>
              <RechartsLabel value={`Fin. (${thresholds.finans_onemsiz})`} position="insideTopRight" fill="#707B7C" fontSize={10} dx={5} dy={8} fontWeight="600" />
            </ReferenceLine>
          </ScatterChart>
        </ResponsiveContainer>
        <div style={{ textAlign: 'center', fontSize: '9px', marginTop: '10px', color: '#666' }}>
          ETKİ HAYATİ {thresholds.etki_hayati} ÖNEMSİZ {thresholds.etki_onemsiz} | FİNANS HAYATİ {thresholds.finans_hayati} ÖNEMSİZ {thresholds.finans_onemsiz}
        </div>
      </div>
    );
  };

  const renderContent = (pageData?: any) => {
    try {
      // Sayfa verisi varsa onu kullan, yoksa orijinal veriyi kullan
      const contentToRender = pageData || data.content;
      
      // Null check
      if (!contentToRender) {
        return <div>İçerik bulunamadı</div>;
      }
      
      // TSRS verisi için özel render (displayNo, soruReferans, soruMadde, cevap)
      if (Array.isArray(contentToRender) && contentToRender.length > 0 && 
          contentToRender[0]?.displayNo && contentToRender[0]?.soruReferans && contentToRender[0]?.soruMadde) {
        return (
          <div style={{ border: '1px solid #333', overflow: 'visible' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '6.5px'
            }}>
              <thead>
                <tr>
                  <th style={{
                    border: '1px solid #333',
                    padding: '3px',
                    textAlign: 'center',
                    background: '#8D6E63',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '6.5px',
                    width: '5%'
                  }}>No</th>
                  <th style={{
                    border: '1px solid #333',
                    padding: '3px',
                    textAlign: 'left',
                    background: '#8D6E63',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '6.5px',
                    width: '15%'
                  }}>Referans</th>
                  <th style={{
                    border: '1px solid #333',
                    padding: '3px',
                    textAlign: 'left',
                    background: '#8D6E63',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '6.5px',
                    width: '40%'
                  }}>Soru</th>
                  <th style={{
                    border: '1px solid #333',
                    padding: '3px',
                    textAlign: 'left',
                    background: '#8D6E63',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '6.5px',
                    width: '40%'
                  }}>Cevap</th>
                </tr>
              </thead>
              <tbody style={{ borderBottom: '1px solid #333' }}>
                {contentToRender.map((item: any, index: number) => (
                  <tr key={index} style={{
                    backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white'
                  }}>
                    <td style={{
                      border: '1px solid #333',
                      padding: '3px',
                      textAlign: 'center',
                      fontSize: '6.5px',
                      fontWeight: 'bold',
                      lineHeight: '1.1'
                    }}>{item.displayNo}</td>
                    <td style={{
                      border: '1px solid #333',
                      padding: '3px',
                      fontSize: '6.5px',
                      lineHeight: '1.1'
                    }}>{item.soruReferans}</td>
                    <td style={{
                      border: '1px solid #333',
                      padding: '3px',
                      fontSize: '6.5px',
                      lineHeight: '1.1'
                    }}>{item.soruMadde}</td>
                    <td style={{
                      border: '1px solid #333',
                      padding: '3px',
                      fontSize: '6.5px',
                      lineHeight: '1.1'
                    }}>{item.cevap || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      
      // Kategori bazlı veri için özel render (birleşik satırlar)
      if (Array.isArray(contentToRender) && contentToRender.length > 0 && contentToRender[0]?.category) {
        let currentCategory = '';
        
        return (
          <div style={{ border: '1px solid #333', overflow: 'visible' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '6.5px'
            }}>
              <thead>
                <tr>
                  <th style={{
                    border: '1px solid #333',
                    padding: '3px',
                    textAlign: 'center',
                    background: '#8D6E63',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '6.5px',
                    width: '5%'
                  }}>No</th>
                  <th style={{
                    border: '1px solid #333',
                    padding: '3px',
                    textAlign: 'left',
                    background: '#8D6E63',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '6.5px',
                    width: '15%'
                  }}>Referans</th>
                  <th style={{
                    border: '1px solid #333',
                    padding: '3px',
                    textAlign: 'left',
                    background: '#8D6E63',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '6.5px',
                    width: '40%'
                  }}>Soru</th>
                  <th style={{
                    border: '1px solid #333',
                    padding: '3px',
                    textAlign: 'left',
                    background: '#8D6E63',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '6.5px',
                    width: '40%'
                  }}>Cevap</th>
                </tr>
              </thead>
              <tbody style={{ borderBottom: '1px solid #333' }}>
                {contentToRender.map((item: any, index: number) => {
                  const showCategoryHeader = item.isFirstInCategory;
                  const categoryRow = showCategoryHeader ? (
                    <tr key={`cat-${index}`}>
                      <td colSpan={4} style={{
                        border: '1px solid #333',
                        padding: '3px 6px',
                        backgroundColor: '#f5f5f5',
                        borderLeft: '4px solid #8D6E63',
                        fontSize: '7px',
                        fontWeight: 'bold',
                        color: '#8D6E63'
                      }}>
                        {item.category}
                      </td>
                    </tr>
                  ) : null;
                  
                  return (
                    <React.Fragment key={index}>
                      {categoryRow}
                      <tr style={{
                        backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white'
                      }}>
                        <td style={{
                          border: '1px solid #333',
                          padding: '3px',
                          textAlign: 'center',
                          fontSize: '6.5px',
                          fontWeight: 'bold',
                          lineHeight: '1.1'
                        }}>{item.displayNo}</td>
                        <td style={{
                          border: '1px solid #333',
                          padding: '3px',
                          fontSize: '6.5px',
                          lineHeight: '1.1'
                        }}>{item.soruReferans}</td>
                        <td style={{
                          border: '1px solid #333',
                          padding: '3px',
                          fontSize: '6.5px',
                          lineHeight: '1.1'
                        }}>{item.soruMadde}</td>
                        <td style={{
                          border: '1px solid #333',
                          padding: '3px',
                          fontSize: '6.5px',
                          lineHeight: '1.1'
                        }}>{item.cevap || '-'}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }
      
      // Survey (Anket) rendering
      if (data.type === 'survey' && contentToRender.anketBasligi) {
        return (
          <div style={{ fontFamily: 'Arial, sans-serif' }}>
            {/* Anket Başlığı */}
            <h1 style={{ fontSize: '18px', fontWeight: 'bold', textAlign: 'center', marginBottom: '16px', color: '#1f2937' }}>
              {contentToRender.anketBasligi}
            </h1>
            
            {/* Giriş Metni */}
            {contentToRender.girisMetni && (
              <div style={{ fontSize: '11px', marginBottom: '20px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px', lineHeight: '1.6' }}>
                {contentToRender.girisMetni}
              </div>
            )}
            
            {/* Bölümler ve Sorular */}
            {contentToRender.bolumler?.map((bolum: any, bolumIndex: number) => (
              <div key={bolumIndex} style={{ marginBottom: '24px' }}>
                <h2 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '12px', color: '#374151', borderBottom: '2px solid #8D6E63', paddingBottom: '4px' }}>
                  {bolum.bolumBasligi}
                </h2>
                
                {bolum.sorular?.map((soru: any, soruIndex: number) => (
                  <div key={soruIndex} style={{ marginBottom: '16px', paddingLeft: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>
                      {bolumIndex + 1}.{soruIndex + 1}. {soru.metin}
                    </div>
                    
                    {/* Likert Scale */}
                    {soru.tip === 'likert1_5' && (
                      <div style={{ marginLeft: '16px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '4px' }}>
                          {[1, 2, 3, 4, 5].map(val => (
                            <label key={val} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px' }}>
                              <span style={{ width: '14px', height: '14px', border: '1px solid #9ca3af', borderRadius: '50%', display: 'inline-block' }}></span>
                              <span>{val}</span>
                            </label>
                          ))}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: '#6b7280', marginTop: '4px' }}>
                          <span>Kesinlikle Katılmıyorum</span>
                          <span>Kesinlikle Katılıyorum</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Açık Uçlu */}
                    {soru.tip === 'acik_uclu' && (
                      <div style={{ marginLeft: '16px', marginTop: '8px' }}>
                        <div style={{ border: '1px solid #d1d5db', borderRadius: '4px', padding: '8px', minHeight: '60px', backgroundColor: '#f9fafb' }}>
                          <span style={{ fontSize: '9px', color: '#9ca3af' }}>Cevabınızı buraya yazınız...</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
            
            {/* Teşekkür Metni */}
            {contentToRender.tesekkurMetni && (
              <div style={{ fontSize: '11px', marginTop: '24px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px', textAlign: 'center', fontStyle: 'italic' }}>
                {contentToRender.tesekkurMetni}
              </div>
            )}
          </div>
        );
      }
      
      // Eğer content zaten bir React element ise direkt render et
      if (React.isValidElement(contentToRender)) {
        return contentToRender;
      }
      
      // Table rendering with topic headers
      if (data.type === 'table' && Array.isArray(contentToRender) && contentToRender.length > 0) {
        // Check if we have grouped data with headers
        const hasHeaders = contentToRender.some((item: any) => item.konuAdi && !item['Soru No']);
        
        if (hasHeaders) {
          // Render grouped content with topic headers
          const elements: React.ReactNode[] = [];
          let currentTable: any[] = [];
          let tableKey = 0;
          
          contentToRender.forEach((item: any, index: number) => {
            if (item.konuAdi && !item['Soru No']) {
              // Render previous table if exists
              if (currentTable.length > 0) {
                const firstRow = currentTable[0];
                elements.push(
                  <table key={`table-${tableKey}`} className="w-full border-collapse" style={{ fontSize: '8px', border: '1px solid #9ca3af', marginBottom: '4px' }}>
                    <thead>
                      <tr className="bg-gray-100">
                        {Object.keys(firstRow).map(key => (
                          <th key={key} className="border border-gray-400 p-2 text-left font-semibold" style={{ 
                            fontSize: '8px',
                            width: key === 'Soru No' ? '8%' : key === 'Soru' ? '46%' : key === 'Cevap' ? '46%' : 'auto'
                          }}>
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentTable.map((row: any, rowIndex: number) => (
                        <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          {Object.values(row).map((value: any, cellIndex: number) => (
                            <td key={cellIndex} className="border border-gray-400 p-2" style={{ fontSize: '8px' }}>
                              {String(value || '-')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
                currentTable = [];
                tableKey++;
              }
              
              // Add topic header with minimal spacing
              elements.push(
                <div key={`header-${index}`} style={{ marginTop: tableKey > 0 ? '8px' : '0', marginBottom: '4px' }}>
                  <h2 style={{ fontSize: '11px', fontWeight: 'bold', color: '#334155', borderBottom: '2px solid #8D6E63', paddingBottom: '2px' }}>
                    {item.konuAdi}
                  </h2>
                </div>
              );
            } else {
              // Add to current table
              currentTable.push(item);
            }
          });
          
          // Render final table if exists
          if (currentTable.length > 0) {
            const firstRow = currentTable[0];
            elements.push(
              <table key={`table-${tableKey}`} className="w-full border-collapse" style={{ fontSize: '8px', border: '1px solid #9ca3af', marginBottom: '4px' }}>
                <thead>
                  <tr className="bg-gray-100">
                    {(data.columns || [
                      { key: 'soru_no', title: 'Soru No', width: '10%' },
                      { key: 'referans', title: 'Referans', width: '20%' },
                      { key: 'soru', title: 'Soru', width: '40%' },
                      { key: 'cevap', title: 'Cevap', width: '30%' }
                    ]).map(col => (
                      <th key={col.key} className="border border-gray-400 p-2 text-left font-semibold" style={{ 
                        fontSize: '8px',
                        width: col.width || 'auto'
                      }}>
                        {col.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentTable.map((row: any, rowIndex: number) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      {(data.columns || [
                        { key: 'soru_no', title: 'Soru No', width: '10%' },
                        { key: 'referans', title: 'Referans', width: '20%' },
                        { key: 'soru', title: 'Soru', width: '40%' },
                        { key: 'cevap', title: 'Cevap', width: '30%' }
                      ]).map((col, cellIndex) => (
                        <td key={cellIndex} className="border border-gray-400 p-2" style={{ fontSize: '8px' }}>
                          {col.render ? col.render(row[col.key]) : String(row[col.key] || '-')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          }
          
          return <div>{elements}</div>;
        } else {
          // Regular table rendering
          const firstRow = contentToRender[0];
          if (!firstRow || typeof firstRow !== 'object') {
            return <div>Geçersiz tablo verisi</div>;
          }
          
          // Columns tanımlı ise sadece belirtilen kolonları göster
          const columnsToShow = data.columns || [
            { key: 'soru_no', title: 'Soru No', width: '10%' },
            { key: 'referans', title: 'Referans', width: '20%' },
            { key: 'soru', title: 'Soru', width: '40%' },
            { key: 'cevap', title: 'Cevap', width: '30%' }
          ];
          
          return (
            <table className="w-full border-collapse" style={{ fontSize: '8px', border: '1px solid #9ca3af' }}>
              <thead>
                <tr className="bg-gray-100">
                  {columnsToShow.map(col => (
                    <th key={col.key} className="border border-gray-400 p-2 text-left font-semibold" style={{ 
                      fontSize: '8px',
                      width: col.width || 'auto'
                    }}>
                      {col.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contentToRender.map((row: any, index: number) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    {columnsToShow.map((col, cellIndex) => (
                      <td key={cellIndex} className="border border-gray-400 p-2" style={{ fontSize: '8px' }}>
                        {col.render ? col.render(row[col.key]) : String(row[col.key] || '-')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }
      }

      // Fallback rendering
      return <div>{String(contentToRender)}</div>;
    } catch (error) {
      console.error('Render content error:', error);
      return <div>Render hatası oluştu</div>;
    }
  };

  try {
    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-full max-h-[95vh] flex flex-col overflow-hidden">
          
          {/* Toolbar */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                📋
              </div>
              <div>
                <h3 className="text-lg font-semibold">{data?.title || 'Önizleme'}</h3>
                {data?.subtitle && <p className="text-sm text-slate-300">{data.subtitle}</p>}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Zoom Controls */}
              <div className="flex items-center gap-1 bg-white bg-opacity-10 rounded-lg p-1">
                <button
                  onClick={() => updateOptions({ zoom: Math.max(50, (options?.zoom || 100) - 25) })}
                  className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-md flex items-center justify-center transition-all"
                >
                  <ZoomOut size={14} />
                </button>
                <span className="px-2 text-sm font-medium min-w-[50px] text-center">{options?.zoom || 100}%</span>
                <button
                  onClick={() => updateOptions({ zoom: Math.min(200, (options?.zoom || 100) + 25) })}
                  className="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-md flex items-center justify-center transition-all"
                >
                  <ZoomIn size={14} />
                </button>
              </div>

              {/* Page Info */}
              <div className="px-3 py-2 bg-slate-600 rounded-lg text-sm font-medium">
                📄 {paginatedContent?.length || 1} Sayfa
              </div>

              {/* Format Toggle */}
              <button
                onClick={() => updateOptions({ 
                  format: (options?.format || 'portrait') === 'portrait' ? 'landscape' : 'portrait' 
                })}
                className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-all"
                title={`Şu an: ${(options?.format || 'portrait') === 'portrait' ? 'Dikey' : 'Yatay'}`}
              >
                🔄 {(options?.format || 'portrait') === 'portrait' ? 'Dikey' : 'Yatay'}
              </button>

              {/* Download PDF */}
              <button
                onClick={downloadPDF}
                disabled={isLoading}
                className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
              >
                <Download size={14} />
                {isLoading ? 'Hazırlanıyor...' : 'PDF'}
              </button>

              {/* Print - Sadece icon */}
              <button
                onClick={print}
                className="w-10 h-10 bg-orange-600 hover:bg-orange-700 rounded-lg flex items-center justify-center transition-all"
                title="Yazdır"
              >
                <Printer size={16} />
              </button>

              {/* Download Excel */}
              <button
                onClick={downloadExcel}
                disabled={isLoading}
                className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                title="Excel olarak indir"
              >
                <FileSpreadsheet size={14} />
                Excel
              </button>

              {/* Close */}
              <button
                onClick={close}
                className="w-8 h-8 bg-red-500 hover:bg-red-600 rounded-lg flex items-center justify-center transition-all"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto bg-white p-6">
            <div className="flex flex-col items-center gap-8" style={{ transform: `scale(${(options?.zoom || 100) / 100})`, transformOrigin: 'top center' }}>
              {paginatedContent?.map((pageContent, pageIndex) => (
                <div 
                  key={pageIndex}
                  className={`preview-content bg-white shadow-lg ${options?.format || 'portrait'}`}
                  style={{
                    width: (options?.format || 'portrait') === 'landscape' ? '297mm' : '210mm',
                    minHeight: (options?.format || 'portrait') === 'landscape' ? '210mm' : '297mm',
                    padding: '12mm',
                    boxSizing: 'border-box',
                    pageBreakAfter: pageIndex < (paginatedContent?.length || 1) - 1 ? 'always' : 'auto',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {/* Header */}
                  <div className="text-center mb-4 pb-3 border-b-2 border-slate-600">
                    <h1 className="text-lg font-bold text-slate-700 mb-1">{data?.companyName || 'Firma'}</h1>
                    <p className="text-xs text-gray-600 mb-1">{data?.year || new Date().getFullYear()} Sürdürülebilirlik Raporu</p>
                    <p className="text-[10px] text-gray-500">{data?.pageTitle || 'Bölüm Adı'}</p>
                  </div>

                  {/* Dynamic Content */}
                  <div className={`content-area ${options?.format || 'portrait'}`} style={{ flex: 1, overflow: 'visible', paddingBottom: '10px', marginBottom: '5px' }}>
                    {React.isValidElement(pageContent) ? pageContent : renderContent(pageContent)}
                    
                    {/* Grafik - sadece son sayfada göster */}
                    {pageIndex === paginatedContent.length - 1 && data.chartData && renderChart()}
                  </div>
                  
                  {/* Sayfa numarası */}
                  {paginatedContent.length > 1 && (
                    <div className="text-center text-[10px] text-gray-400 mt-2">
                      Sayfa {pageIndex + 1} / {paginatedContent.length}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="text-center text-[10px] text-gray-500 mt-3 pt-2 border-t">
                    {data?.moduleName || 'Modül'} • Rapor Tarihi: {new Date().toLocaleDateString('tr-TR')}
                  </div>
                </div>
              )) || <div>Veri yüklüyor...</div>}
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  } catch (error) {
    console.error('UniversalPreviewModal render error:', error);
    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-96 p-6">
          <h3 className="text-lg font-semibold mb-4">Hata Oluştu</h3>
          <p className="text-gray-600 mb-4">Önizleme yüklenirken bir hata oluştu.</p>
          <button
            onClick={close}
            className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
          >
            Kapat
          </button>
        </div>
      </div>,
      document.body
    );
  }
};

export default UniversalPreviewModal;
